/* eslint-disable */
import React, { useMemo } from 'react';
import { STAGE_OPTIONS, stageKeyFor } from './devImportUtils';
import { STAGE_COLORS, DONE_COLOR } from '../../utils/devCardUtils';
import styles from './DevImport.module.css';

// DNA classification palette (Phase 2/3).
const INK    = '#8e887b';
const GREEN  = '#3a8845';
const DIM    = '#b8b2a4';
const AMBER  = '#a86810';
const VIOLET = '#7c3aed';
const RED    = '#c0503f';

const ICON_WARN = <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" /></svg>;

const CLASSIFICATION_META = {
  new:                { label: 'New',           color: GREEN },
  exact_duplicate:    { label: 'Exact dup',     color: DIM },
  soft_conflict:      { label: 'Soft conflict', color: AMBER },
  internal_duplicate: { label: 'In-file dup',   color: VIOLET },
  invalid:            { label: 'Invalid',       color: RED },
};

// UI label → RPC user_action (matches ACTION_TO_RPC in useDevImportClassify.js).
const ACTIONS_REGULAR = [
  { id: 'skip',         label: 'Skip',          color: INK },
  { id: 'replace',      label: 'Replace',       color: VIOLET },
  { id: 'merge',        label: 'Merge',         color: GREEN },
  { id: 'review_later', label: 'Review later',  color: AMBER },
];

const ACTIONS_NEW = [
  { id: 'import',       label: 'Import',        color: GREEN },
  { id: 'skip',         label: 'Skip',          color: INK },
];

const ACTIONS_INVALID = [
  { id: 'skip',         label: 'Skip',          color: INK },
];

function actionsFor(classification) {
  if (classification === 'new') return ACTIONS_NEW;
  if (classification === 'invalid') return ACTIONS_INVALID;
  return ACTIONS_REGULAR;
}

function Pill({ active, color, label, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={styles.pill}
      style={active
        ? { background: color, borderColor: color, color: '#fff' }
        : (disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined)}
    >
      {label}
    </button>
  );
}

function formatCell(v) {
  if (v === null || v === undefined) return <span className={styles.cellMuted}>—</span>;
  if (typeof v === 'object') return <code className={styles.diffCode}>{JSON.stringify(v)}</code>;
  return String(v);
}

function DiffBody({ diff }) {
  const entries = useMemo(() => {
    if (!diff) return [];
    return Object.entries(diff).slice(0, 20);
  }, [diff]);

  if (!entries.length) {
    return (
      <div className={styles.diffEmpty}>
        No field-level differences detected. Likely a case-insensitive or whitespace-only difference in <code className={styles.diffCode}>dev_number</code>.
      </div>
    );
  }

  return (
    <table className={styles.diffTable}>
      <thead>
        <tr>
          <th className={styles.diffTh}>Field</th>
          <th className={styles.diffTh}>DB (existing)</th>
          <th className={`${styles.diffTh} ${styles.diffThFile}`}>File (new)</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([field, pair]) => {
          const [dbVal, fileVal] = Array.isArray(pair) ? pair : [null, null];
          return (
            <tr key={field} className={styles.diffRow}>
              <td className={`${styles.diffTd} ${styles.diffTdField}`}>{field}</td>
              <td className={`${styles.diffTd} ${styles.diffTdOld}`}>{formatCell(dbVal)}</td>
              <td className={`${styles.diffTd} ${styles.diffTdNew}`}>{formatCell(fileVal)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function recommendationFor(classification) {
  if (classification === 'new') return 'New dev — will be inserted.';
  if (classification === 'exact_duplicate') return 'Exact duplicate — safe to skip.';
  if (classification === 'soft_conflict') return 'Some fields differ. Merge to take the new values; Replace to start clean; Skip to keep DB as-is.';
  if (classification === 'invalid') return 'Missing dev_number — cannot be imported.';
  return '';
}

export function DevConflictCard({ stagingRow, decision, onAction, onStageChange, updateMutation }) {
  const { staging_id, row_index, raw_data, classification, diff, conflict_with_id } = stagingRow || {};
  const meta = CLASSIFICATION_META[classification] || { label: classification, color: INK };
  const actions = actionsFor(classification);
  const devNumber = raw_data?.dev_number || raw_data?.item_code || '(no dev_number)';
  const diffCount = diff ? Object.keys(diff).length : 0;

  // T-1039 — STAGE row derivations
  const currentStageStatus = raw_data?.stage_status || '';
  const currentFullStatus  = raw_data?.full_status  || '';
  const currentStageKey    = stageKeyFor(currentStageStatus, currentFullStatus);
  const currentStageOpt    = STAGE_OPTIONS.find(o => o.key === currentStageKey) || STAGE_OPTIONS[3];
  const stageTheme = currentStageOpt.key === 'done'
    ? DONE_COLOR
    : (STAGE_COLORS[currentStageOpt.stage_status] || STAGE_COLORS['Drawing stage']);
  const wasInvalid = raw_data?._stage_was_valid === false;
  const excelRaw   = raw_data?._stage_excel_raw || '';
  const isPending  = !!(updateMutation && updateMutation.isPending);

  return (
    <div className={styles.confCard} style={{ borderLeft: `4px solid ${meta.color}` }}>
      {/* Head */}
      <div className={styles.confCardHead}>
        <span className={styles.rowIdx}>#{row_index ?? '?'}</span>
        <span className={styles.devNo}>{devNumber}</span>
        <span className={styles.chip} style={{ color: meta.color, background: meta.color + '15', borderColor: meta.color + '40' }}>{meta.label}</span>
        {classification === 'soft_conflict' && diffCount > 0 && (
          <span className={styles.diffCount}>{diffCount} field{diffCount === 1 ? '' : 's'} differ</span>
        )}
      </div>

      {/* T-1039 — STAGE override row */}
      <div className={styles.stageRow}>
        <div className={styles.stageLabel}>Stage</div>
        <div className={styles.stageMain}>
          <div className={styles.stageMainRow}>
            <select
              value={currentStageKey}
              onChange={e => onStageChange && onStageChange(staging_id, e.target.value)}
              disabled={isPending}
              className={styles.stageSelect}
              style={{
                background: stageTheme.light,
                color: stageTheme.text,
                border: `1px solid ${stageTheme.sash}`,
                opacity: isPending ? 0.6 : 1,
                cursor: isPending ? 'wait' : 'pointer',
              }}
            >
              {STAGE_OPTIONS.map(opt => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
            {wasInvalid && (
              <span className={styles.autoChip}>{ICON_WARN} Auto-corrected</span>
            )}
          </div>
          <div className={styles.stageFrom}>
            <span>From Excel:</span>
            {excelRaw ? (
              <code className={styles.stageRaw}>"{excelRaw}"</code>
            ) : (
              <em>column not present — defaulted</em>
            )}
            <span>→</span>
            <span className={styles.stageFull}>{currentFullStatus}</span>
          </div>
        </div>
      </div>

      {/* Diff */}
      {(classification === 'soft_conflict' || classification === 'exact_duplicate') && (
        <DiffBody diff={diff} />
      )}

      {/* Actions */}
      <div className={styles.actions}>
        {actions.map(a => (
          <Pill
            key={a.id}
            label={a.label}
            color={a.color}
            active={decision === a.id}
            onClick={() => onAction && onAction(a.id)}
          />
        ))}
        <span className={styles.rec}>{recommendationFor(classification)}</span>
      </div>
    </div>
  );
}
